/**
 * Pure compat-decision logic (MEIS-13220, design.md D3/D4).
 *
 * The frontend's compatibility state is decided EXCLUSIVELY by comparing the
 * bundled `API_COMPAT_VERSION` (`@msys/common`) against a freshly fetched
 * `/config.json`'s `apiCompatVersion`. This module never sees a raw
 * `X-Api-Compat-Version` response header — the header is only ever a trigger
 * to fetch `/config.json` again (see `ApolloClientProvider.tsx`'s
 * `customFetch` and `DeployCompatibility.tsx`), never an input to this
 * decision. That keeps a rolling backend deploy (interleaved old/new
 * instances) from flapping the state: only the single, atomically-published
 * `/config.json` can move it.
 */
export type CompatState =
  | "inert"
  | "current"
  | "compatible-update"
  | "incompatible";

export interface CompatCheckInput {
  /** The running tab's own bundled `API_COMPAT_VERSION`. */
  ownCompatVersion: number;
  /** `apiCompatVersion` from a fresh /config.json fetch; absent on feature envs, local dev, pre-rollout images. */
  publishedCompatVersion: number | undefined;
  /** The `version` sha this tab last compared against (undefined on the very first check). */
  previousVersion: string | undefined;
  /** `version` from the same fresh /config.json fetch. */
  publishedVersion: string | undefined;
}

export function decideCompatState(input: CompatCheckInput): CompatState {
  const {
    ownCompatVersion,
    publishedCompatVersion,
    previousVersion,
    publishedVersion,
  } = input;

  if (publishedCompatVersion === undefined) return "inert";

  // Any mismatch, either direction — including a rollback to an older
  // compat version — means this tab no longer matches the deployed
  // backend (design.md D3 comparison semantics).
  if (publishedCompatVersion !== ownCompatVersion) return "incompatible";

  if (
    previousVersion !== undefined &&
    publishedVersion !== undefined &&
    previousVersion !== publishedVersion
  ) {
    return "compatible-update";
  }

  return "current";
}
